// src/Tryouts.js - Spring/Fall Tryouts - Gladiator Games inspired
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import useDocumentMeta from './useDocumentMeta';
import logo from './assets/images/gladiators-hero.png';
import aaVideo from './assets/videos/AA Video.mp4';
import './GladiatorGames.css';
import './Tryouts.css';
import { db, collection, addDoc } from './firebase';

// Create a $40 Payment Link in Stripe Dashboard, then add REACT_APP_TRYOUT_STRIPE_LINK to .env
const TRYOUT_STRIPE_LINK = process.env.REACT_APP_TRYOUT_STRIPE_LINK || 'https://buy.stripe.com/REPLACE_WITH_40_LINK';

const Tryouts = () => {
  const [formData, setFormData] = useState({
    parentFirstName: '',
    parentLastName: '',
    email: '',
    phone: '',
    skaterName: '',
    position: '',
    birthYear: '',
    currentHockeySituation: '',
    currentTeam: '',
    lookingForFromGladiators: '',
    yearsOfExperience: '',
    excitedAbout: [],
    futureEvents: '',
    programImportance: [],
    howDidYouHear: '',
    readyForTryout: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const [questionForm, setQuestionForm] = useState({ name: '', email: '', phone: '', question: '' });
  const [questionSubmitting, setQuestionSubmitting] = useState(false);
  const [questionMessage, setQuestionMessage] = useState('');

  useDocumentMeta({
    title: 'Join the Next Era - AA Tryouts | Glass City Gladiators',
    description: 'Glass City Gladiators are moving to AA hockey for Spring and Fall 2026. Elite development, affordable costs, player-first culture. Tryouts March 15 at The Ice House.',
    canonical: 'https://gcghockey.com/tryouts',
    ogType: 'website',
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox' && (name === 'excitedAbout' || name === 'programImportance')) {
      setFormData((prev) => ({
        ...prev,
        [name]: checked ? [...prev[name], value] : prev[name].filter((item) => item !== value),
      }));
    } else {
      setFormData((prev) => {
        const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
        if (name === 'currentHockeySituation' && value !== 'Playing on a travel team') {
          next.currentTeam = '';
          next.lookingForFromGladiators = '';
        }
        return next;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    if (formData.programImportance.length === 0) {
      setSubmitMessage('Please select at least one item for "What\'s most important in a hockey program?"');
      setIsSubmitting(false);
      return;
    }
    if (formData.currentHockeySituation === 'Playing on a travel team' && (!formData.currentTeam?.trim() || !formData.lookingForFromGladiators?.trim())) {
      setSubmitMessage('Please complete the travel team questions: which team you\'re coming from and what you\'re looking for from the Gladiators.');
      setIsSubmitting(false);
      return;
    }
    if (formData.excitedAbout.length === 0) {
      setSubmitMessage('Please select at least one item for "What are you most excited about?"');
      setIsSubmitting(false);
      return;
    }

    try {
      const parentName = `${formData.parentFirstName} ${formData.parentLastName}`.trim();
      await addDoc(collection(db, 'tryouts'), {
        ...formData,
        participantName: formData.skaterName,
        parentName,
        submittedAt: new Date().toISOString(),
        tryoutDate: '2026-03-15',
        tryoutTime: '11:30 AM - 1:30 PM',
        fee: 40,
        status: 'pending',
      });

      setSubmitMessage('SUCCESS! A payment window will open. Complete your $40 tryout fee to secure your spot.');
      setFormData({
        parentFirstName: '',
        parentLastName: '',
        email: '',
        phone: '',
        skaterName: '',
        position: '',
        birthYear: '',
        currentHockeySituation: '',
        currentTeam: '',
        lookingForFromGladiators: '',
        yearsOfExperience: '',
        excitedAbout: [],
        futureEvents: '',
        programImportance: [],
        howDidYouHear: '',
        readyForTryout: false,
      });

      window.open(TRYOUT_STRIPE_LINK, '_blank');
    } catch (error) {
      setSubmitMessage('Oops! Something went wrong. Please try again or email us directly.');
      console.error('Submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleQuestionChange = (e) => {
    const { name, value } = e.target;
    setQuestionForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleQuestionSubmit = async (e) => {
    e.preventDefault();
    setQuestionSubmitting(true);
    setQuestionMessage('');
    try {
      await addDoc(collection(db, 'tryout_questions'), {
        ...questionForm,
        submittedAt: new Date().toISOString(),
      });
      setQuestionMessage('Thanks! We\'ll get back to you within 24 hours.');
      setQuestionForm({ name: '', email: '', phone: '', question: '' });
    } catch (error) {
      setQuestionMessage('Something went wrong. Please try again or email us directly.');
      console.error('Question submission error:', error);
    } finally {
      setQuestionSubmitting(false);
    }
  };

  return (
    <div className="gladiator-games">
      <div className="games-bg-overlay"></div>
      <div className="games-grid-pattern"></div>

      {/* Video Hero - Top of Page */}
      <section className="tryouts-video-section">
        <video
          src={aaVideo}
          autoPlay
          muted
          loop
          playsInline
          className="tryouts-hero-video"
        />
      </section>

      <section className="games-hero">
        <motion.div
          className="games-hero-content"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <motion.h1
            className="games-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Join the Next Era of Gladiators Hockey
          </motion.h1>

          <motion.p
            className="games-intro-text"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            The Glass City Gladiators are entering the most exciting chapter in our program&apos;s history.
          </motion.p>

          <motion.p
            className="games-intro-text"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            For Spring and Fall 2026, the Gladiators are officially moving to AA hockey, raising the level of competition while staying true to what makes us different: elite development, a positive culture, and affordability that puts families first.
          </motion.p>

          <motion.p
            className="games-intro-text games-intro-highlight"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            This isn&apos;t just a tryout. It&apos;s an opportunity to be part of a program that&apos;s growing the right way.
          </motion.p>

          <motion.div
            className="games-date-badge"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.7 }}
          >
            <div className="games-date-main">March 15, 2026</div>
            <div className="games-date-time">11:30 AM - 1:30 PM</div>
            <div className="games-date-location">
              <span className="games-venue-name">The Ice House</span>
              <span className="games-venue-address">1258 W Alexis Rd, Toledo, OH 43612</span>
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          className="games-scroll-indicator"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
        >
          <div className="games-scroll-text">Scroll to Learn More</div>
          <div className="games-scroll-arrow">↓</div>
        </motion.div>
      </section>

      <section className="games-details">
        <div className="games-container">
          <motion.h2
            className="games-section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Why Gladiators?
          </motion.h2>

          <div className="games-features-grid">
            <motion.div
              className="games-feature-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="games-feature-icon" aria-hidden="true"></div>
              <h3>AA Level Development</h3>
              <p>We are building competitive AA teams focused on skating, hockey IQ, and confidence, not shortcuts or politics.</p>
            </motion.div>

            <motion.div
              className="games-feature-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="games-feature-icon" aria-hidden="true"></div>
              <h3>Affordable by Design</h3>
              <p>We believe great hockey development should be accessible. Our model keeps costs realistic without sacrificing coaching, ice time, or experience.</p>
            </motion.div>

            <motion.div
              className="games-feature-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="games-feature-icon" aria-hidden="true"></div>
              <h3>Player-First Culture</h3>
              <p>Every player matters here. We develop athletes, teammates, and people through accountability, effort, and support.</p>
            </motion.div>

            <motion.div
              className="games-feature-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <div className="games-feature-icon" aria-hidden="true"></div>
              <h3>High-Energy, Fun Environment</h3>
              <p>We compete hard, train with purpose, and still make hockey exciting. Kids love being here, and that matters.</p>
            </motion.div>
          </div>

          <motion.h2
            className="games-section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            What We&apos;re Looking For
          </motion.h2>

          <motion.div
            className="tryouts-looking-for"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="tryouts-looking-intro">We&apos;re seeking players who:</p>
            <ul>
              <li>Love the game and want to be coached</li>
              <li>Are ready to compete at the AA level</li>
              <li>Value team culture and work ethic</li>
              <li>Want a program that develops them long-term</li>
            </ul>
            <p className="tryouts-looking-outro">Spring and Fall tryouts are open to players ready to take the next step and families looking for something different from the traditional travel hockey experience.</p>
          </motion.div>

          <motion.div
            className="tryouts-build-cta"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="games-section-title">Be Part of the Build</h2>
            <p>The Gladiators aren&apos;t just moving up a level. We&apos;re building something special.</p>
            <p>If you&apos;re looking for AA hockey without AA headaches, for development without inflated costs, and for a program that&apos;s ambitious, fun, and community-driven,</p>
            <p className="tryouts-build-highlight">We&apos;d love to see you at tryouts.</p>
          </motion.div>

          <motion.div
            className="games-price-banner"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="games-price-label">Tryout Fee</div>
            <div className="games-price-amount">$40</div>
            <div className="games-price-includes">
              Skater evaluation<br />
              Ice time<br />
              Secure your spot
            </div>
          </motion.div>
        </div>
      </section>

      <section className="games-registration">
        <div className="games-container">
          <motion.div
            className="games-form-wrapper"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="games-form-title">Register for Tryouts</h2>
            <p className="games-form-subtitle">Complete the form and pay the $40 fee to secure your spot. A payment window will open after you submit. Please use Chrome we found the Safari browser doesn't have the pop up on mobile. .</p>

            <form onSubmit={handleSubmit} className="games-form">
              <div className="games-form-section">
                <h3 className="games-form-section-title">Parent/Guardian Information</h3>
                <div className="games-form-row">
                  <div className="games-form-group">
                    <label htmlFor="parentFirstName">First Name *</label>
                    <input type="text" id="parentFirstName" name="parentFirstName" value={formData.parentFirstName} onChange={handleChange} required placeholder="John" />
                  </div>
                  <div className="games-form-group">
                    <label htmlFor="parentLastName">Last Name *</label>
                    <input type="text" id="parentLastName" name="parentLastName" value={formData.parentLastName} onChange={handleChange} required placeholder="Smith" />
                  </div>
                </div>
                <div className="games-form-row">
                  <div className="games-form-group">
                    <label htmlFor="email">Email *</label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required placeholder="john.smith@email.com" />
                  </div>
                  <div className="games-form-group">
                    <label htmlFor="phone">Phone Number *</label>
                    <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} required placeholder="(419) 555-0123" />
                  </div>
                </div>
              </div>

              <div className="games-form-section">
                <h3 className="games-form-section-title">Skater Information</h3>
                <div className="games-form-group">
                  <label htmlFor="skaterName">Skater&apos;s Full Name *</label>
                  <input type="text" id="skaterName" name="skaterName" value={formData.skaterName} onChange={handleChange} required placeholder="Alex Smith" />
                </div>
                <div className="games-form-row">
                  <div className="games-form-group">
                    <label htmlFor="position">Position *</label>
                    <select id="position" name="position" value={formData.position} onChange={handleChange} required>
                      <option value="">Select Position</option>
                      <option value="Forward">Forward</option>
                      <option value="Defense">Defense</option>
                      <option value="Goalie">Goalie</option>
                    </select>
                  </div>
                  <div className="games-form-group">
                    <label htmlFor="birthYear">Birth Year *</label>
                    <select id="birthYear" name="birthYear" value={formData.birthYear} onChange={handleChange} required>
                      <option value="">Select Year</option>
                      <option value="2012">2012</option>
                      <option value="2013">2013</option>
                      <option value="2014">2014</option>
                      <option value="2015">2015</option>
                    </select>
                  </div>
                </div>
                <div className="games-form-row">
                  <div className="games-form-group">
                    <label htmlFor="currentHockeySituation">Current hockey situation? *</label>
                    <select id="currentHockeySituation" name="currentHockeySituation" value={formData.currentHockeySituation} onChange={handleChange} required>
                      <option value="">Select Option</option>
                      <option value="Playing on a travel team">Playing on a travel team</option>
                      <option value="Playing on a house/rec team">Playing on a house/rec team</option>
                      <option value="Between teams / looking for a team">Between teams / looking for a team</option>
                      <option value="New to competitive hockey">New to competitive hockey</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="games-form-group">
                    <label htmlFor="yearsOfExperience">Years of organized hockey experience? *</label>
                    <select id="yearsOfExperience" name="yearsOfExperience" value={formData.yearsOfExperience} onChange={handleChange} required>
                      <option value="">Select Experience</option>
                      <option value="1-2 years">1-2 years</option>
                      <option value="3-4 years">3-4 years</option>
                      <option value="5+ years">5+ years</option>
                    </select>
                  </div>
                </div>
                {formData.currentHockeySituation === 'Playing on a travel team' && (
                  <>
                    <div className="games-form-group" style={{ marginTop: '1rem' }}>
                      <label htmlFor="currentTeam">Which team are you coming from? *</label>
                      <input type="text" id="currentTeam" name="currentTeam" value={formData.currentTeam} onChange={handleChange} required placeholder="e.g. Toledo Cherokee 12U" />
                    </div>
                    <div className="games-form-group">
                      <label htmlFor="lookingForFromGladiators">What are you looking for from the Gladiators that you felt you weren&apos;t getting at your current team? *</label>
                      <textarea id="lookingForFromGladiators" name="lookingForFromGladiators" value={formData.lookingForFromGladiators} onChange={handleChange} required placeholder="Tell us what matters to you..." rows={4} />
                    </div>
                  </>
                )}
              </div>

              <div className="games-form-section">
                <h3 className="games-form-section-title">Tell Us More</h3>
                <div className="games-form-group">
                  <label>What are you most excited about? (Select all that apply) *</label>
                  <div className="games-checkbox-group">
                    {['Playing travel hockey', 'Developing skills', 'Team culture', 'Competition', 'Meeting new players'].map((opt) => (
                      <div key={opt} className="games-form-checkbox">
                        <input type="checkbox" id={`excited-${opt.replace(/\s/g, '-')}`} name="excitedAbout" value={opt} checked={formData.excitedAbout.includes(opt)} onChange={handleChange} />
                        <label htmlFor={`excited-${opt.replace(/\s/g, '-')}`}>{opt}</label>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="games-form-group">
                  <label htmlFor="futureEvents">Interested in future seasons? *</label>
                  <select id="futureEvents" name="futureEvents" value={formData.futureEvents} onChange={handleChange} required>
                    <option value="">Select Option</option>
                    <option value="Spring only">Spring only</option>
                    <option value="Fall only">Fall only</option>
                    <option value="Both Spring and Fall">Both Spring and Fall</option>
                  </select>
                </div>
                <div className="games-form-group">
                  <label>What&apos;s most important in a hockey program? (Select all that apply) *</label>
                  <div className="games-checkbox-group">
                    {['High-level competition', 'Strong coaching', 'Team culture', 'Flexible schedule', 'Tournament travel', 'Local convenience'].map((opt) => (
                      <div key={opt} className="games-form-checkbox">
                        <input type="checkbox" id={`importance-${opt.replace(/\s/g, '-')}`} name="programImportance" value={opt} checked={formData.programImportance.includes(opt)} onChange={handleChange} />
                        <label htmlFor={`importance-${opt.replace(/\s/g, '-')}`}>{opt}</label>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="games-form-group">
                  <label htmlFor="howDidYouHear">How did you hear about us? *</label>
                  <select id="howDidYouHear" name="howDidYouHear" value={formData.howDidYouHear} onChange={handleChange} required>
                    <option value="">Select Option</option>
                    <option value="Current Gladiators family">Current Gladiators family</option>
                    <option value="Social media">Social media</option>
                    <option value="Friend/word of mouth">Friend/word of mouth</option>
                    <option value="Hockey rink/coach recommendation">Hockey rink/coach recommendation</option>
                    <option value="Web search">Web search</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div className="games-form-section">
                <div className="games-form-checkbox">
                  <input type="checkbox" id="readyForTryout" name="readyForTryout" checked={formData.readyForTryout} onChange={handleChange} required />
                  <label htmlFor="readyForTryout">
                    <strong>Yes, I&apos;m ready to try out!</strong>
                  </label>
                </div>
              </div>

              <motion.button type="submit" className="games-submit-btn" disabled={isSubmitting} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                {isSubmitting ? 'Submitting...' : 'Register & Pay $40'}
              </motion.button>

              {submitMessage && (
                <motion.div className={`games-submit-message ${submitMessage.includes('SUCCESS') ? 'success' : 'error'}`} initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                  {submitMessage}
                </motion.div>
              )}
            </form>
          </motion.div>
        </div>
      </section>

      <section className="games-faq">
        <div className="games-container">
          <motion.h2 className="games-section-title" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            FAQ
          </motion.h2>
          <div className="games-faq-grid">
            <motion.div className="games-faq-item" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
              <h4>When & where?</h4>
              <p>March 15, 2026 from 11:30 AM to 1:30 PM at The Ice House, 1258 W Alexis Rd, Toledo, OH 43612.</p>
            </motion.div>
            <motion.div className="games-faq-item" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }}>
              <h4>What&apos;s the $40 for?</h4>
              <p>Your tryout fee covers ice time and evaluation. Payment is required to secure your spot.</p>
            </motion.div>
            <motion.div className="games-faq-item" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.2 }}>
              <h4>12U & 14U?</h4>
              <p>Birth years 2012-2015. Our coaches will evaluate and place players on the appropriate team.</p>
            </motion.div>
            <motion.div className="games-faq-item" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.25 }}>
              <h4>How are tryouts structured?</h4>
              <p>Each tryout will consist of a 2-hour on-ice session. This extended ice time allows our coaching staff to properly evaluate players for both our 12U and 14U teams, including skating ability, hockey sense, compete level, and coachability.</p>
            </motion.div>
            <motion.div className="games-faq-item" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.3 }}>
              <h4>Do you have roster spots available?</h4>
              <p>Yes. We 100% have open spots for both our 12U and 14U teams for the Spring and Fall 2026 seasons. Tryouts are competitive, but we are actively building rosters.</p>
            </motion.div>
            <motion.div className="games-faq-item" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.35 }}>
              <h4>What can we expect for the Spring season?</h4>
              <p>The Spring season is designed to develop players while keeping the schedule balanced for families.</p>
              <p>Spring program includes:</p>
              <ul>
                <li>Weekly on-ice sessions</li>
                <li>Structured off-ice conditioning and development</li>
                <li>Two tournaments</li>
                <li>Focus on skill development, skating, and preparation for Fall AA hockey</li>
              </ul>
            </motion.div>
            <motion.div className="games-faq-item" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.4 }}>
              <h4>What can we expect for the Fall season?</h4>
              <p>While we are still finalizing our structure for the fall season, our current season included the following:</p>
              <ul>
                <li>Over 100 on-ice touches throughout the season</li>
                <li>Nine tournaments</li>
                <li>Consistent practices, games, and development sessions</li>
                <li>Emphasis on performance, growth, and team identity</li>
              </ul>
            </motion.div>
            <motion.div className="games-faq-item" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.45 }}>
              <h4>What is the expected financial commitment?</h4>
              <p>We are committed to keeping high-level hockey affordable.</p>
              <ul>
                <li>Spring: approximately $600</li>
                <li>Fall: approximately $2,000-$2,500</li>
              </ul>
              <p>Exact pricing will be finalized once rosters, ice schedules, and tournaments are confirmed.</p>
            </motion.div>
            <motion.div className="games-faq-item" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.5 }}>
              <h4>What about goalies?</h4>
              <p>Goalies are a huge part of our program.</p>
              <ul>
                <li>Goalies pay 50% of the skater cost for both Spring and Fall</li>
                <li>Dedicated goalie development is included throughout the season</li>
              </ul>
            </motion.div>
          </div>

          <motion.section
            className="tryouts-ask-section"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="games-section-title">Have a question you don&apos;t see an answer to?</h2>
            <p className="tryouts-ask-intro">Ask your question here. We&apos;ll get back to you within 24 hours.</p>
            <form onSubmit={handleQuestionSubmit} className="tryouts-ask-form">
              <div className="tryouts-ask-row">
                <div className="games-form-group">
                  <label htmlFor="questionName">Name *</label>
                  <input type="text" id="questionName" name="name" value={questionForm.name} onChange={handleQuestionChange} required placeholder="Your name" />
                </div>
                <div className="games-form-group">
                  <label htmlFor="questionEmail">Email *</label>
                  <input type="email" id="questionEmail" name="email" value={questionForm.email} onChange={handleQuestionChange} required placeholder="your@email.com" />
                </div>
                <div className="games-form-group">
                  <label htmlFor="questionPhone">Phone *</label>
                  <input type="tel" id="questionPhone" name="phone" value={questionForm.phone} onChange={handleQuestionChange} required placeholder="(419) 555-0123" />
                </div>
              </div>
              <div className="games-form-group">
                <label htmlFor="questionMessage">Your question *</label>
                <textarea id="questionMessage" name="question" value={questionForm.question} onChange={handleQuestionChange} required placeholder="Ask us anything..." rows={4} />
              </div>
              <motion.button type="submit" className="games-submit-btn" disabled={questionSubmitting} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                {questionSubmitting ? 'Sending...' : 'Send question'}
              </motion.button>
              {questionMessage && (
                <p className={`tryouts-ask-message ${questionMessage.includes('Thanks') ? 'success' : 'error'}`}>{questionMessage}</p>
              )}
            </form>
          </motion.section>
        </div>
      </section>
    </div>
  );
};

export default Tryouts;
